import type {
  NotificationRuleDefinition,
  NotificationRuleModel,
  NotificationRuleOperator,
} from "@rubi/api-generated";

/**
 * Helpers for the Advanced Notifications screen.
 *
 * `rv2_notification_rule` stores a single `status` and a single `survey_class` per row
 * (both nullable, meaning "any"). Operators asked for multi-select on both, so the UI works
 * in terms of *rule groups*: one group is a set of backing rows that are identical apart from
 * their `status`/`survey_class`, and saving a group expands it back into the cartesian product
 * of the selected actions and report types. If those columns ever become arrays server-side,
 * grouping collapses to a 1:1 mapping and only this module has to change.
 */

/** An empty `statuses`/`surveyClasses` array means "any" — matching the nullable columns. */
export interface NotificationRuleGroup {
  /** Stable identity derived from everything except status/survey_class. */
  key: string;
  name: string;
  statuses: string[];
  surveyClasses: string[];
  emailAddresses: string[];
  ruleDefinition: NotificationRuleDefinition;
  isDefault: boolean;
  priority: number;
  /** Backing rows, ascending by id so re-grouping is stable across refetches. */
  rules: NotificationRuleModel[];
}

export interface NotificationRuleTarget {
  status: string | null;
  surveyClass: string | null;
}

export interface NotificationRuleSavePlan {
  creates: NotificationRuleTarget[];
  updates: { ruleId: string; target: NotificationRuleTarget }[];
  deletes: string[];
}

export const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "submitted", label: "Submitted" },
  { value: "resubmitted", label: "Resubmitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "escalated", label: "Escalated" },
  { value: "modification_requested", label: "Modification Requested" },
  { value: "supplemental_approved", label: "Supplemental Approved" },
];

export const OPERATOR_OPTIONS: {
  value: NotificationRuleOperator;
  label: string;
  /** Reads as a sentence fragment in the plain-English summary. */
  phrase: string;
}[] = [
  { value: "equals", label: "Equals", phrase: "is" },
  { value: "contains", label: "Contains", phrase: "contains" },
  { value: "greater_than", label: "Greater Than", phrase: "is greater than" },
  {
    value: "greater_than_or_equal",
    label: "Greater Than or Equal",
    phrase: "is at least",
  },
  { value: "less_than", label: "Less Than", phrase: "is less than" },
  {
    value: "less_than_or_equal",
    label: "Less Than or Equal",
    phrase: "is at most",
  },
  { value: "in_list", label: "In List", phrase: "is one of" },
];

export const NUMERIC_OPERATORS: NotificationRuleOperator[] = [
  "greater_than",
  "greater_than_or_equal",
  "less_than",
  "less_than_or_equal",
];

/** Sentinel `rule_definition` written for default rules, which carry no real condition. */
export const DEFAULT_RULE_DEFINITION: NotificationRuleDefinition = {
  field: "_default",
  operator: "equals",
  value: true,
};

export const statusLabel = (status: string): string =>
  STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;

export const operatorPhrase = (operator: NotificationRuleOperator): string =>
  OPERATOR_OPTIONS.find((option) => option.value === operator)?.phrase ??
  operator;

const targetKey = (target: NotificationRuleTarget): string =>
  `${target.status ?? ""}\0${target.surveyClass ?? ""}`;

const ruleTargetKey = (rule: NotificationRuleModel): string =>
  targetKey({
    status: rule.status ?? null,
    surveyClass: rule.survey_class ?? null,
  });

const sortedUnique = (values: string[]): string[] =>
  [...new Set(values)].sort((a, b) => a.localeCompare(b));

const groupKeyFor = (rule: NotificationRuleModel): string =>
  JSON.stringify([
    rule.name,
    rule.is_default,
    rule.priority,
    rule.is_default ? null : rule.rule_definition,
    sortedUnique(rule.email_addresses ?? []),
  ]);

const buildGroup = (
  key: string,
  rules: NotificationRuleModel[],
): NotificationRuleGroup => {
  const [first] = rules;
  return {
    key,
    name: first.name,
    // A null column means "any", which the UI represents as an empty array.
    statuses: sortedUnique(
      rules.flatMap((rule) => (rule.status ? [rule.status] : [])),
    ),
    surveyClasses: sortedUnique(
      rules.flatMap((rule) => (rule.survey_class ? [rule.survey_class] : [])),
    ),
    emailAddresses: first.email_addresses ?? [],
    ruleDefinition: first.rule_definition,
    isDefault: first.is_default,
    priority: first.priority,
    rules,
  };
};

/**
 * Rows only merge when they cover an exact cartesian product — every row either scopes a column
 * or none of them do, and there is one row per combination. Anything else (a stray "any report
 * type" row alongside per-type rows, or a duplicate) would silently lose a row when the group is
 * re-expanded on save, so those rows stay separate.
 */
const isCartesianProduct = (rules: NotificationRuleModel[]): boolean => {
  const statuses = new Set<string | null>(rules.map((r) => r.status ?? null));
  const classes = new Set<string | null>(
    rules.map((r) => r.survey_class ?? null),
  );
  if (statuses.size > 1 && statuses.has(null)) return false;
  if (classes.size > 1 && classes.has(null)) return false;
  // Counting rows alone would let a duplicated pair stand in for a missing one, showing coverage
  // the agency does not have and repointing the duplicate on the next save.
  if (new Set(rules.map(ruleTargetKey)).size !== rules.length) return false;
  if (rules.length !== statuses.size * classes.size) return false;
  return true;
};

/**
 * Collapse backing rows into the groups the UI edits. Rows only merge when they are identical
 * apart from `status`/`survey_class`, so a group can always be re-expanded without losing data.
 */
export const groupNotificationRules = (
  rules: NotificationRuleModel[],
): NotificationRuleGroup[] => {
  const buckets = new Map<string, NotificationRuleModel[]>();

  for (const rule of [...rules].sort((a, b) => a.id.localeCompare(b.id))) {
    const key = groupKeyFor(rule);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(rule);
    } else {
      buckets.set(key, [rule]);
    }
  }

  const groups: NotificationRuleGroup[] = [];
  for (const [key, bucket] of buckets) {
    if (isCartesianProduct(bucket)) {
      groups.push(buildGroup(key, bucket));
    } else {
      groups.push(
        ...bucket.map((rule) => buildGroup(`${key}#${rule.id}`, [rule])),
      );
    }
  }

  return groups.sort(
    (a, b) => a.priority - b.priority || a.name.localeCompare(b.name),
  );
};

/** Cartesian product of the selected actions and report types; `[]` expands to a single `null`. */
export const expandRuleGroupTargets = (
  statuses: string[],
  surveyClasses: string[],
): NotificationRuleTarget[] => {
  const statusValues: (string | null)[] = statuses.length ? statuses : [null];
  const classValues: (string | null)[] = surveyClasses.length
    ? surveyClasses
    : [null];

  return statusValues.flatMap((status) =>
    classValues.map((surveyClass) => ({ status, surveyClass })),
  );
};

/**
 * Work out the minimal set of POST/PATCH/DELETE calls that turns `existing` into `desired`.
 * Rows are reused wherever possible so ids — and therefore `time_created` — survive an edit.
 */
export const planRuleGroupSave = (
  existing: NotificationRuleModel[],
  desired: NotificationRuleTarget[],
): NotificationRuleSavePlan => {
  const unmatchedRows = [...existing];
  const plan: NotificationRuleSavePlan = {
    creates: [],
    updates: [],
    deletes: [],
  };
  const leftoverTargets: NotificationRuleTarget[] = [];

  // First pass: reuse the row that already has this exact (status, survey_class).
  for (const target of desired) {
    const index = unmatchedRows.findIndex(
      (rule) => ruleTargetKey(rule) === targetKey(target),
    );
    if (index === -1) {
      leftoverTargets.push(target);
      continue;
    }
    const [rule] = unmatchedRows.splice(index, 1);
    plan.updates.push({ ruleId: rule.id, target });
  }

  // Second pass: repoint whatever rows are left over before creating new ones.
  for (const target of leftoverTargets) {
    const rule = unmatchedRows.shift();
    if (rule) {
      plan.updates.push({ ruleId: rule.id, target });
    } else {
      plan.creates.push(target);
    }
  }

  plan.deletes = unmatchedRows.map((rule) => rule.id);
  return plan;
};

const joinWithOr = (values: string[]): string => {
  if (values.length <= 1) return values[0] ?? "";
  if (values.length === 2) return `${values[0]} or ${values[1]}`;
  return `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
};

const formatConditionValue = (value: unknown): string => {
  if (Array.isArray(value)) return joinWithOr(value.map(String));
  if (typeof value === "number") return value.toLocaleString("en-US");
  return String(value ?? "");
};

/** Drops the JSONPath root so `$.property.value` reads as `property.value` in prose. */
const formatFieldPath = (field: string): string =>
  field.replace(/^\$\.?/, "") || field;

export interface DescribeRuleInput {
  statuses: string[];
  surveyClasses: string[];
  emailAddresses: string[];
  ruleDefinition: NotificationRuleDefinition;
  isDefault: boolean;
}

/**
 * Plain-English restatement of a rule, e.g.
 * "Send an email to records@agency.ca when a Theft report is approved and property.value is
 * less than 5,000."
 */
export const describeNotificationRule = (
  rule: DescribeRuleInput,
  reportTypeLabels: Record<string, string> = {},
): string => {
  const recipients = rule.emailAddresses.length
    ? joinWithOr(rule.emailAddresses)
    : "…";

  const subject = rule.surveyClasses.length
    ? `a ${joinWithOr(
        rule.surveyClasses.map((c) => reportTypeLabels[c] ?? c),
      )} report`
    : "any report";

  const trigger = rule.statuses.length
    ? `is ${joinWithOr(rule.statuses.map((s) => statusLabel(s).toLowerCase()))}`
    : "changes status";

  const condition = rule.isDefault
    ? ", but only when no other rule matches"
    : ` and ${formatFieldPath(rule.ruleDefinition.field)} ${operatorPhrase(
        rule.ruleDefinition.operator,
      )} ${formatConditionValue(rule.ruleDefinition.value)}`;

  return `Send an email to ${recipients} when ${subject} ${trigger}${condition}.`;
};
